// =============================================================================
// Helpers for turning the stored contact_details object into clickable links.
//   • WhatsApp/Viber/Phone numbers stored as { country_code, number } strings.
//   • Telegram stored as { username } (with or without leading '@').
//   • Email stored as { address }.
// =============================================================================

const stripNonDigits = (s) => (s || "").replace(/\D/g, "");

/**
 * Convert { country_code, number } → bare international-format digits, e.g.
 *   { country_code: "+382", number: "67-123 456" }  →  "38267123456"
 */
export function toInternational({ country_code, number } = {}) {
  return stripNonDigits(country_code) + stripNonDigits(number);
}

/**
 * Return a visually-masked phone string. Keeps the country code and first
 * two digits visible, replaces the rest with •.
 *   "+38267123456"  →  "+382 67 •• ••"
 */
export function maskPhone({ country_code, number } = {}) {
  const cc = (country_code || "").trim();
  const n = stripNonDigits(number);
  if (!n) return cc || "";
  const head = n.slice(0, 2);
  // Spec example: "+382 67 • • • •" — single bullets separated by spaces.
  const tail = n.slice(2).split("").map(() => "•").join(" ");
  return `${cc} ${head} ${tail}`.trim();
}

export function whatsappHref(details, msg) {
  const num = toInternational(details);
  if (!num) return null;
  const text = msg || "Hello, I found your profile on Relaxora. Are you available?";
  return `https://wa.me/${num}?text=${encodeURIComponent(text)}`;
}

export function viberHref(details) {
  const num = toInternational(details);
  if (!num) return null;
  return `viber://chat?number=%2B${num}`;
}

export function telegramHref(username) {
  const u = (username || "").trim().replace(/^@+/, "");
  if (!u) return null;
  return `https://t.me/${u}`;
}

export function telHref(details) {
  const num = toInternational(details);
  if (!num) return null;
  return `tel:+${num}`;
}

export function mailtoHref(address) {
  const a = (address || "").trim();
  if (!a) return null;
  return `mailto:${a}`;
}

/**
 * Convenience — returns the list of channels we have BOTH enabled AND filled
 * for a profile. Used by the public profile page to render contact buttons.
 */
export function activeContactChannels(profile) {
  const prefs = profile?.contact_prefs || {};
  const det = profile?.contact_details || {};
  const out = [];
  if (prefs.whatsapp && det.whatsapp?.number) out.push("whatsapp");
  if (prefs.telegram && det.telegram?.username) out.push("telegram");
  if (prefs.phone    && det.phone?.number)    out.push("phone");
  if (prefs.viber    && det.viber?.number)    out.push("viber");
  if (prefs.email    && det.email?.address)   out.push("email");
  return out;
}
